/**
 * Zahlungen (Stitch: maestro_zahlungen): KPI-Reihe + auftragssortierte Gruppen.
 * Es gibt bewusst keinen globalen Payments-Endpunkt (tenant-scoped pro Event) —
 * daher werden die Zahlungen der Events parallel geladen und zusammengefuehrt.
 *
 * Umbau 27.08.2026 (Antoine, Projekt-Sichten): "Die Menüpunkte Zahlungen/Mail-Protokoll müssen
 * auftragssortiert sein. Also ein Auftrag öffnen, dann alle Zahlungen." Die frühere flache
 * Tabelle (eine Zeile je Zahlung, quer über alle Aufträge) ist einer Gruppen-Liste gewichen —
 * ein zugeklappter Kopf je Auftrag mit Summe, aufklappbar zu den einzelnen Zahlungszeilen.
 * Dieselbe Bauart wie `pages/mail-protokoll.tsx`.
 */
import { useMemo, useState } from "react";
import { anlassAnzeige } from "../ui/format";
import { Link, useSearchParams } from "react-router-dom";
import { useApi } from "../hooks/useApi";
import { deckungsSatz, type Deckung } from "../lib/zahlungsdeckung-anzeige";
import { angebotPfad, vorgangPfad, type ZahlungsReiter } from "../lib/pfade";
import { useMitHerkunft } from "../lib/rueckweg";
import { nettoBezahltCents } from "../lib/zahlungs-kennzahlen";
import type { Customer, Payment } from "../ui/types";
import { useContentRefetch } from "../ui/CopilotProvider";
import {
  PAYMENT_STATUS,
  PAYMENT_TYPE_LABEL,
  type StatusMeta,
  ZAHLUNG_STATUS_REIHENFOLGE,
  costAcceptanceMfaHinweis,
  formatDate,
  formatDateTime,
  money,
} from "../ui/format";
import {
  Card,
  Drawer,
  EmptyState,
  ErrorNote,
  Icon,
  Input,
  KeineAngabe,
  LoadingRows,
  KpiCard,
  ListFooter,
  ListenReiter,
  PageHeader,
  SortierKopf,
  SortierMenue,
  StatusPill,
  ButtonRouterLink,
} from "../ui/components";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { useNarrowViewport } from "../hooks/useNarrowViewport";
import { useUrlZustand } from "../hooks/useUrlZustand";
import { oeffneGlobaleSuche } from "../lib/globale-suche";
import { openAuthenticatedFile } from "../lib/files";

interface PaymentWithEvent extends Payment {
  eventLabel: string;
  eventNumber: string | null;
  customerId: string;
  /**
   * Punkt 67 (20.09.2026): Gehoert diese Zahlung zu einer Angebotsfassung, und wurde die
   * gewaehlt? `null` heisst „gehoert zum Vorgang, nicht zu einer Fassung" — Bestandszeilen und
   * Zahlplaene des Betriebs. Kommt aus `GET /api/payments` (Linksverbund auf die Fassung); die
   * Oberflaeche entscheidet damit nichts selbst, sie liest nur ab.
   */
  optionGewaehlt?: boolean | null;
}

/**
 * Punkt 67: Zaehlt diese Zeile fuer die Summen des Vorgangs?
 *
 * Seit dem Versand je Fassung (Migration 455) liegen zu einem Vorgang mehrere offene
 * Anzahlungen — eine je Angebotsfassung. Gelten wird am Ende genau eine. Wer alle summiert,
 * zeigt einen Offen-Betrag, den niemand je schuldet. Dieselbe Regel steht serverseitig in
 * `lib/zahlungsdeckung.ts`; hier wird sie NICHT nachgerechnet, sondern nur auf dieselbe Weise
 * angewandt, damit Liste und Deckungshinweis nicht auseinanderlaufen.
 */
export function zaehltFuerDenVorgang(p: PaymentWithEvent): boolean {
  if (p.optionId == null) return true;
  return p.optionGewaehlt === true;
}

/* --------------------------- Detail-Drawer (Quick-Win 2026-07) --------------------------- */
/** Antwort von GET /api/payments/:id/detail — reines Lese-Aggregat, siehe apps/api/src/routes/payments.ts. */
interface PaymentDetailRow {
  id: string;
  eventId: string;
  amountCents: number;
  currency: string;
  paymentType: string;
  status: string;
  stripeCheckoutSessionId: string | null;
  stripePaymentIntentId: string | null;
  stripePaymentLinkId: string | null;
  checkoutUrl: string | null;
  dueDate: string | null;
  paidAt: string | null;
  createdAt: string;
  updatedAt: string;
}
interface PaymentDetailEvent {
  id: string;
  number: string | null;
  occasion: string | null;
  currentOfferVersion: number;
}
interface PaymentActivityEntry {
  id: string;
  actorType: "user" | "customer" | "system";
  actorLabel: string | null;
  action: string;
  details: Record<string, unknown> | null;
  createdAt: string;
}
interface PaymentDetailCostAcceptance {
  id: string;
  status: "draft" | "sent" | "signed" | "declined" | "withdrawn" | "signed_ungueltig";
  statusReason: string | null;
  signerName: string | null;
  /** Migration 357 ("alles, was von eSign zurueckkommt" — Antoine, 03.09.2026). Beide koennen
   *  NULL sein (Bestandszeile vor Migration 357, oder der Provider lieferte sie nicht) — die UI
   *  blendet dann aus statt zu raten. */
  signerEmail: string | null;
  signerMobile: string | null;
  mfaMethods: string[] | null;
  amountGrossCents: number | null;
  signedAt: string | null;
  signedPdfUrl: string | null;
  /** M-24 Nachtrag: nicht-leer <=> eigenes Archiv gefuellt - Signal fuer den owner/admin-
   *  geschuetzten Download ueber /api/events/:id/cost-acceptance/pdf. */
  signedPdfSha256: string | null;
  createdAt: string;
}
interface PaymentDetailInvoice {
  id: string;
  invoiceType: "invoice" | "deposit" | "final" | "credit";
  status: "draft" | "open" | "paid" | "voiding" | "voided" | "failed";
  invoiceNumber: string | null;
  amountGrossCents: number | null;
  sentAt: string | null;
  createdAt: string;
  pdfUrl: string | null;
}
interface PaymentDetailData {
  payment: PaymentDetailRow;
  event: PaymentDetailEvent | null;
  timeline: PaymentActivityEntry[];
  costAcceptance: PaymentDetailCostAcceptance | null;
  invoice: PaymentDetailInvoice | null;
}

/** Anzeigetext je Activity-Actioncode (lib/activity-log.ts) — Spiegel des Musters aus event-detail.tsx,
 *  hier nur die zahlungsbezogenen Codes (details.paymentId-gefiltert liefert nur diese). */
const PAYMENT_ACTIVITY_LABEL: Record<string, string> = {
  "payment.created": "Zahlung angelegt",
  "payment.plan_created": "Zahlungsplan angelegt",
  "payment.link_created": "Zahlungslink erstellt",
  "payment.link_sent": "Zahlungslink per E-Mail versendet",
  "payment.link_invalidated": "Zahlungslink entwertet (Betrag/Typ geändert)",
  // H-02: Der Text haengt jetzt am Inhalt - siehe zahlungsEingangText(). Der Eintrag hier bleibt
  // als Rueckfall fuer Alt-Eintraege ohne Deckungszahlen.
  "payment.succeeded": "Zahlung eingegangen",
  "payment.refund_initiated": "Rückerstattung bei Stripe veranlasst",
  "payment.refunded_external": "Extern erstattet verbucht",
  "payment.tombstoned": "Zahlung storniert (Link entwertet)",
  "payment.deleted": "Zahlung gelöscht",
};
function activityLabel(action: string): string {
  return PAYMENT_ACTIVITY_LABEL[action] ?? action;
}

/**
 * H-02: Ein Zahlungseingang sagt jetzt, ob damit Schluss ist.
 *
 * Seit der Deckungspruefung kann eine erfolgreiche Zahlung den Vorgang unveraendert lassen. Ohne
 * diesen Zusatz stuende dort nur "Zahlung eingegangen", der Vorgang bliebe auf "Angebot
 * angenommen" — und nirgends stuende, warum. Genau diese Stille war der eigentliche Schaden:
 * der Restbetrag geriet aus dem Blick.
 *
 * Die Zahlen kommen aus demselben Aktivitaetseintrag (server-seitig, aus derselben Transaktion wie
 * der Statuswechsel). Fehlen sie — Alt-Eintraege oder Vorgaenge ohne Gesamtbetrag —, bleibt es beim
 * schlichten Text. Nichts wird geschaetzt.
 */
function zahlungsEingangText(ev: PaymentActivityEntry): string {
  if (ev.action !== "payment.succeeded" || !ev.details) return activityLabel(ev.action);
  const offen = typeof ev.details.openCents === "number" ? ev.details.openCents : null;
  if (offen === null) return activityLabel(ev.action);
  if (offen <= 0) return "Zahlung eingegangen — vollständig bezahlt";
  return `Zahlung eingegangen — noch offen: ${money(offen)}`;
}
const ACTOR_LABEL: Record<PaymentActivityEntry["actorType"], string> = { user: "Team", customer: "Kunde", system: "System" };

const CA_STATUS_LABEL: Record<PaymentDetailCostAcceptance["status"], StatusMeta> = {
  draft: { label: "Entwurf", tone: "neutral" },
  sent: { label: "Gesendet – wartet auf Unterschrift", tone: "warn" },
  signed: { label: "Signiert – verbindlich", tone: "success" },
  declined: { label: "Abgelehnt", tone: "neutral" },
  withdrawn: { label: "Zurückgezogen", tone: "neutral" },
  // sql/770: unterschrieben, waehrend das Angebot nach dem Versand geaendert war - Nachweis, aber nicht verbindlich.
  signed_ungueltig: { label: "Unterschrieben, ungültig – Angebot wurde geändert", tone: "warn" },
};
const INV_STATUS_LABEL: Record<PaymentDetailInvoice["status"], StatusMeta> = {
  draft: { label: "Entwurf", tone: "neutral" },
  open: { label: "Erstellt – offen", tone: "warn" },
  paid: { label: "Bezahlt", tone: "success" },
  voiding: { label: "Wird storniert …", tone: "warn" },
  voided: { label: "Storniert", tone: "neutral" },
  failed: { label: "Fehlgeschlagen", tone: "danger" },
};
const INV_TYPE_LABEL: Record<PaymentDetailInvoice["invoiceType"], string> = {
  invoice: "Rechnung",
  deposit: "Anzahlung",
  final: "Schlussrechnung",
  credit: "Gutschrift",
};

/** Eckdaten-Zelle im Drawer (gleiches Muster wie der Anfragen-Drawer: Caps-Label über dem Wert). */
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">{label}</div>
      <div className="mt-1 text-[13px] font-medium text-on-surface dark:text-inverse-on-surface">{value}</div>
    </div>
  );
}

/** Gekürzte Referenz-ID (Stripe-Objekte) — gleiche Kurzform wie die Tabellenzeile (erste 8/letzte 4 Zeichen). */
function shortRef(v: string): string {
  return v.length > 16 ? `${v.slice(0, 8)}…${v.slice(-4)}` : v;
}

/** Eine Auftrags-Gruppe der Liste mit ihren Kennzahlen (Listenstandard, Punkt 95: danach wird sortiert). */
interface ZahlungsGruppe {
  eventId: string;
  zeilen: PaymentWithEvent[];
  kunde: string;
  /** Früheste Fälligkeit der offenen (zählenden) Posten, `null` = nichts offen/keine Fälligkeit. */
  faelligkeit: string | null;
  offenSumme: number;
  bezahltSumme: number;
  hatUeberfaellige: boolean;
  /** Führender Posten für die Status-Pille: überfällig zuerst, sonst der früheste Status im Ablauf. */
  statusZeile: PaymentWithEvent;
}

/** Listenstandard (Brief § 3): sortierbare Spalten der Gruppenköpfe. Posten IN einer Gruppe
 *  behalten ihre Reihenfolge; ohne Sortierung gilt die bisherige (erstes Auftauchen). */
const GRUPPEN_SPALTEN: SortSpalte<ZahlungsGruppe>[] = [
  { key: "kunde", label: "Kunde · Vorgang", type: "text", accessor: (g) => (g.kunde === "–" ? null : g.kunde) },
  { key: "faelligkeit", label: "Fälligkeit", type: "date", accessor: (g) => g.faelligkeit },
  { key: "status", label: "Status", type: "status", accessor: (g) => g.statusZeile.status, statusOrder: ZAHLUNG_STATUS_REIHENFOLGE },
  { key: "offen", label: "Offen", type: "money", accessor: (g) => g.offenSumme },
];
/** Gemeinsames Spaltenraster von Kopf und Gruppenzeilen (Kunde · Fälligkeit · Status · Offen). */
const GRUPPEN_RASTER = "grid grid-cols-[minmax(0,1fr)_120px_210px_190px]";
const GRUPPEN_ZELLE = "min-w-0 px-[7px] first:pl-[18px] last:pr-[18px]";

/** Handy-Karten (Handy-Mockup 26.09.2026): Sortierung als Menü statt Spaltenköpfen, gleiche Schlüssel. */
const GRUPPEN_SORTIERUNG = [
  { label: "Standard", key: null },
  { label: "Fällig", key: "faelligkeit", dir: "asc" },
  { label: "Kunde A–Z", key: "kunde", dir: "asc" },
  { label: "Status", key: "status", dir: "asc" },
  { label: "Offen ↓", key: "offen", dir: "desc" },
] as const;

const STATUS_FILTERS: { key: ZahlungsReiter; label: string }[] = [
  { key: "all", label: "Alle" },
  { key: "open", label: "Offen" },
  { key: "paid", label: "Bezahlt" },
  { key: "failed", label: "Fehlgeschlagen" },
  { key: "refunded", label: "Erstattet" },
];

/** Gehoert eine Zahlung in den Reiter? (Eine Quelle fuer Liste UND Reiter-Zaehler.) */
function imReiter(p: { status: string }, reiter: ZahlungsReiter): boolean {
  if (reiter === "open") return p.status === "pending" || p.status === "processing";
  if (reiter === "paid") return p.status === "succeeded";
  if (reiter === "failed") return p.status === "failed";
  if (reiter === "refunded") return p.status === "refunded";
  return true;
}

export default function PaymentsPage() {
  const mitHier = useMitHerkunft(); // Punkt 115: Spruenge in Vorgang/Kunde merken „Zahlungen“ als Herkunft
  // H1 (Bughunt 2026-07-11): EIN aggregierter, tenant-scoped Endpunkt (Join events) statt des
  // fragilen N+1-Fan-outs (ein HTTP-Request je Event). Damit kann kein einzelnes Event mehr die
  // Gesamtliste entwerten — der irreführende "x von N konnten nicht geladen werden"-Banner entfällt.
  // Z0.4: Die Deckung kommt je VORGANG mit der Antwort (gerechnet in
  // `apps/api/src/lib/zahlungsdeckung.ts`, derselben Stelle wie auf der Vorgangsseite). Sie haengt
  // bewusst nicht an der Zahlungszeile - eine Zeile ist ein Posten, die Deckung eine Eigenschaft
  // des Vorgangs.
  // Migration 357 / Antoines Vorgabe (03.09.2026): "bei den Zahlungen muss die Kostenübernahme
  // ebenfalls vermerkt sein ... sichtbar in der Liste, nicht erst nach einem Klick". Status +
  // signedAt je Vorgang kommen im selben Umschlag mit (server-seitig aus derselben Rechenstelle
  // wie der Einzelabruf, s. routes/payments.ts) — dieselbe Bauart wie deckungJeVorgang.
  const paymentsQ = useApi<PaymentWithEvent[], { deckungJeVorgang?: Record<string, Deckung>; costAcceptanceJeVorgang?: Record<string, { status: PaymentDetailCostAcceptance["status"]; signedAt: string | null }> }>(
    "/api/payments",
  );
  const deckungJeVorgang = paymentsQ.zusatz?.deckungJeVorgang ?? {};
  const costAcceptanceJeVorgang = paymentsQ.zusatz?.costAcceptanceJeVorgang ?? {};
  const customersQ = useApi<Customer[]>("/api/customers");

  // Quick-Win (2026-07): Klick auf eine Zahlungszeile öffnet den Detail-Drawer statt (wie bisher)
  // nur zum Event zu verlinken. Der Detail-Request läuft erst beim Öffnen (kein Vorab-Laden aller
  // Details für die ganze Liste) — gleiches On-Demand-Muster wie der Anfragen-Drawer.
  const [selected, setSelected] = useState<PaymentWithEvent | null>(null);
  const detailQ = useApi<PaymentDetailData>(selected ? `/api/payments/${selected.id}/detail` : null);
  const detail = detailQ.data;

  // Review-Befund 31.07.2026: Der Assistent meldet Zahlungs-Aenderungen an vier Stellen - und
  // niemand hoerte zu. Der Betreiber liess eine Zahlung per Chat anlegen und sah seine eigene
  // Aktion nicht. Der offene Detail-Drawer wird mitgeladen, sonst zeigte er weiter den alten Stand.
  useContentRefetch("payment", () => {
    paymentsQ.reload();
    detailQ.reload();
  });

  const customerName = useMemo(() => {
    const byCustomer = new Map((customersQ.data ?? []).map((c) => [c.id, c.name]));
    return (customerId: string) => byCustomer.get(customerId) || "–";
  }, [customersQ.data]);

  // CX-Audit 2026-07-17: Status-Filter aus der URL (?filter=open|paid|…) — Dashboard-KPIs
  // („Offene Beträge", „Umsatz Monat") verlinken gezielt hierher; Reload/Zurueck erhält den Filter.
  // Punkt 115 (Paket 3): Reiter UND Suche stehen in der Adresse (`useUrlZustand`) — nach „Zurück“
  // vom Vorgang sind beide wieder da; `?q=` bleibt der Einstieg der Kunden-KPI „Offene Posten“.
  const [params, setParams] = useSearchParams();
  const [zustand, setzeZustand] = useUrlZustand({ filter: "all", q: "" });
  const narrow = useNarrowViewport();
  const statusFilter: ZahlungsReiter = STATUS_FILTERS.some((f) => f.key === zustand.filter)
    ? (zustand.filter as ZahlungsReiter)
    : "all";
  const setStatusFilter = (key: ZahlungsReiter) => setzeZustand({ filter: key });
  const search = zustand.q;
  const setSearch = (q: string) => setzeZustand({ q });
  // Punkt 115 (Paket 1): `?vorgang=` grenzt auf einen Vorgang ein (Abrechnung › „Alle Zahlungen“),
  // `?von=`/`?vonName=` (Herkunft) zeigt der PageHeader selbst als „← Zurück zu …“ (Paket 2).
  const vorgangFilter = params.get("vorgang");
  const vorgangFilterAufheben = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("vorgang");
        return next;
      },
      { replace: true },
    );

  const error = paymentsQ.error;
  const loading = paymentsQ.loading;
  const allRows = paymentsQ.data ?? [];
  const now = new Date();
  const overdueIds = new Set(
    allRows.filter((p) => ["pending", "processing"].includes(p.status) && p.dueDate && new Date(p.dueDate) < now).map((p) => p.id),
  );
  /** H11: "Erstattet" blau statt neutral, "Überfällig" (abgeleitet aus Faelligkeit) rot statt amber. */
  function paymentMeta(p: PaymentWithEvent): StatusMeta {
    if (overdueIds.has(p.id)) return { label: "Überfällig", tone: "neutral", icon: "schedule" };
    if (p.status === "refunded") return { label: "Erstattet", tone: "info" };
    return PAYMENT_STATUS[p.status] ?? { label: p.status, tone: "neutral" };
  }
  /** Punkt 115 (Paket 1): Vorgangsfilter + Suche wirken VOR den Reitern — die Reiter-Zaehler
   *  zaehlen also die Suchtreffer, nicht den Gesamtbestand (sonst sieht man nicht, dass der
   *  gesuchte Kunde unter „Bezahlt“ liegt, waehrend „Offen“ leer ist). */
  const suchTreffer = useMemo(() => {
    let list = allRows;
    if (vorgangFilter) list = list.filter((p) => p.eventId === vorgangFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((p) =>
        [customerName(p.customerId), p.eventNumber ?? "", p.eventLabel].some((s) => s.toLowerCase().includes(q)),
      );
    }
    return list;
  }, [allRows, vorgangFilter, search, customerName]);
  // N-5: Zähler „Offen" muss zur gefilterten Liste passen — beide lesen `imReiter`.
  const statusCounts = useMemo(
    () =>
      Object.fromEntries(STATUS_FILTERS.map((f) => [f.key, suchTreffer.filter((p) => imReiter(p, f.key)).length])) as Record<
        ZahlungsReiter,
        number
      >,
    [suchTreffer],
  );
  const rows = useMemo(() => suchTreffer.filter((p) => imReiter(p, statusFilter)), [suchTreffer, statusFilter]);
  /** Auftragssortiert (27.08.2026): eine Gruppe je Vorgang, Reihenfolge = erstes Auftauchen in
   *  `rows` (Z0.4: dieselbe Reihenfolge, die vorher schon "erste sichtbare Zeile ihres Vorgangs"
   *  fuer den Deckungshinweis annahm — hier wird daraus die tatsaechliche Gruppierung).
   *  Listenstandard (Punkt 95): die Kennzahlen je Gruppe entstehen hier einmal, damit Anzeige und
   *  Sortierung dieselben Werte lesen. Punkt 67: Anzahlungen zu nicht gewaehlten Fassungen bleiben
   *  aussen vor (`zaehltFuerDenVorgang`), bezahlt zaehlt netto (Punkt 93, `nettoBezahltCents`). */
  const gruppen: ZahlungsGruppe[] = (() => {
    const map = new Map<string, PaymentWithEvent[]>();
    for (const p of rows) {
      const arr = map.get(p.eventId);
      if (arr) arr.push(p);
      else map.set(p.eventId, [p]);
    }
    return Array.from(map.entries()).map(([eventId, zeilen]) => {
      const zaehlende = zeilen.filter(zaehltFuerDenVorgang);
      const offene = zaehlende.filter((p) => p.status === "pending" || p.status === "processing");
      const faelligkeiten = offene.map((p) => p.dueDate).filter((d): d is string => !!d).sort();
      const ueberfaellig = zeilen.find((p) => overdueIds.has(p.id));
      const rang = (p: PaymentWithEvent) => ZAHLUNG_STATUS_REIHENFOLGE[p.status] ?? Number.MAX_SAFE_INTEGER;
      const basis = zaehlende.length > 0 ? zaehlende : zeilen;
      return {
        eventId,
        zeilen,
        kunde: customerName(zeilen[0]!.customerId),
        faelligkeit: faelligkeiten[0] ?? null,
        offenSumme: offene.reduce((s, p) => s + p.amountCents, 0),
        bezahltSumme: zaehlende.filter((p) => p.status === "succeeded").reduce((s, p) => s + nettoBezahltCents(p), 0),
        hatUeberfaellige: ueberfaellig != null,
        statusZeile: ueberfaellig ?? basis.reduce((a, b) => (rang(b) < rang(a) ? b : a)),
      };
    });
  })();
  const sort = useListSort("zahlungen", GRUPPEN_SPALTEN);
  const sichtbareGruppen = sort.sortiere(gruppen);
  /** Welche Auftrags-Gruppen aufgeklappt sind — Vorgabe zu (Antoine: "ein Auftrag öffnen, dann
   *  alle Zahlungen"), damit die Seite bei vielen Aufträgen nicht sofort wieder eine lange Liste
   *  ist. Eine aktive Suche/ein Statusfilter klappt automatisch alle Treffer auf — sonst müsste
   *  man nach dem Filtern jede Gruppe einzeln erneut öffnen. */
  const [manuellAufgeklappt, setManuellAufgeklappt] = useState<Set<string>>(new Set());
  const gefiltert = statusFilter !== "all" || search.trim().length > 0 || !!vorgangFilter;
  function toggleGruppe(eventId: string) {
    setManuellAufgeklappt((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }
  /*
   * Punkt 67, Nachtrag 20.09.2026: DIE KOPFKACHELN RECHNETEN AUF DER UNGEFILTERTEN LISTE.
   *
   * Die Gruppen weiter unten wenden `zaehltFuerDenVorgang` an, die Kacheln darueber taten es
   * nicht — auf DERSELBEN Seite standen damit zwei verschiedene Wahrheiten. Zwei Fassungen à
   * 150 €, der Gast hat noch nicht gewaehlt: die Kachel „Offen" zeigte 300 €, obwohl noch gar
   * nicht feststeht, welche der beiden je gelten wird. Richtig sind 0 € — es gibt nichts zu
   * fordern, solange die Wahl aussteht.
   *
   * Eine Kennzahl ueber der Liste muss dieselbe Regel anwenden wie die Liste, sonst ist sie
   * keine Zusammenfassung, sondern ein Widerspruch.
   */
  const zaehlendeRows = allRows.filter(zaehltFuerDenVorgang);
  const monthPaid = zaehlendeRows
    .filter(
      (p) =>
        p.status === "succeeded" &&
        p.paidAt &&
        new Date(p.paidAt).getMonth() === now.getMonth() &&
        new Date(p.paidAt).getFullYear() === now.getFullYear(),
    )
    .reduce((s, p) => s + nettoBezahltCents(p), 0);
  const open = zaehlendeRows.filter((p) => ["pending", "processing"].includes(p.status));
  const openSum = open.reduce((s, p) => s + p.amountCents, 0);
  const overdue = open.filter((p) => overdueIds.has(p.id));
  const overdueSum = overdue.reduce((s, p) => s + p.amountCents, 0);
  // Punkt 38 (Migration 484): Voll-Erstattungen UND Teil-Rueckerstattungen noch bezahlter Zeilen.
  const refunded = zaehlendeRows.reduce(
    (s, p) => s + (p.status === "refunded" ? p.amountCents : p.status === "succeeded" ? (p.refundedAmountCents ?? 0) : 0),
    0,
  );
  const kpiBereit = paymentsQ.data != null;
  const monthLabel = now.toLocaleDateString("de-DE", { month: "long", year: "numeric" });

  return (
    <>
      <PageHeader
        title="Zahlungen"
        subtitle={`${monthLabel} · Übersicht aller Transaktionen über alle Vorgänge.`}
      />

      {/* KPI-Reihe (Stitch: Bezahlt Monat / Offen / Erstattet) — H9: Icon-Farben semantisch */}
      {/* Handy: eine wischbare Reihe statt drei gestapelter Kacheln — die Liste beginnt früher. */}
      <div className="tab-scroll -mx-4 mb-4 flex snap-x gap-3 px-4 sm:mx-0 sm:mb-6 sm:grid sm:grid-cols-1 sm:gap-4 sm:px-0 md:grid-cols-3 max-sm:[&>*]:w-[72%] max-sm:[&>*]:shrink-0 max-sm:[&>*]:snap-start">
        {/* Ohne Daten (Laden/Fehler) „–“ statt 0,00 € — eine Null wäre eine erfundene Aussage. */}
        <KpiCard label={`Bezahlt · ${monthLabel}`} value={kpiBereit ? money(monthPaid) : "–"} icon="payments" iconTone="success" />
        <KpiCard
          label={kpiBereit ? `Offen · ${open.length.toLocaleString("de-DE")} Rechnung${open.length === 1 ? "" : "en"}` : "Offen"}
          value={kpiBereit ? money(openSum) : "–"}
          icon="account_balance_wallet"
          iconTone="warn"
          delta={overdue.length > 0 ? { text: `${overdue.length.toLocaleString("de-DE")} überfällig`, tone: "down" } : undefined}
        />
        <KpiCard label={`Erstattet · ${monthLabel}`} value={kpiBereit ? money(refunded) : "–"} icon="undo" iconTone="info" />
      </div>

      {/* L9: echte Tabellen-Textsuche (Kunde/Beleg) — nicht die globale ⌘K-Palette */}
      <div className="mb-4">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={narrow ? "Kunde, Beleg …" : "Zahlungen durchsuchen (Kunde, Beleg) …"}
          className="w-full max-w-sm"
        />
        {narrow && (
          <SortierMenue className="mt-2" optionen={GRUPPEN_SORTIERUNG} sortKey={sort.sortKey} dir={sort.dir} onWahl={sort.setze} />
        )}
        {vorgangFilter && (
          <p className="mt-2 flex flex-wrap items-center gap-2 text-[12.5px] text-secondary dark:text-secondary-fixed-dim">
            <Icon name="filter_alt" className="text-[16px]" />
            Nur Zahlungen zu Vorgang {allRows.find((p) => p.eventId === vorgangFilter)?.eventNumber ?? "(ohne Nummer)"}
            <button
              type="button"
              onClick={vorgangFilterAufheben}
              className="rounded-md font-semibold text-primary hover:underline dark:text-primary-fixed"
            >
              Filter aufheben
            </button>
          </p>
        )}
      </div>

      {/* Auftragssortierte Gruppen (Umbau 27.08.2026) — je Auftrag ein aufklappbarer Kopf mit
          Summe, darunter die einzelnen Zahlungszeilen. Listenstandard (Punkt 95): Reiter in der
          Karte, sortierbarer, stehenbleibender Kopf für die GRUPPEN, Fußzeile, einheitliche Zustände. */}
      <Card className="overflow-hidden">
        {/* H8: Status-Filter-Tabs — Zähler aus den Suchtreffern (Punkt 115), `?filter` in der URL,
            die Sortierung bleibt beim Wechsel erhalten (useListSort). */}
        <ListenReiter
          label="Zahlungen nach Status"
          reiter={STATUS_FILTERS.map((f) => ({ ...f, anzahl: statusCounts[f.key] }))}
          aktiv={statusFilter}
          onWahl={setStatusFilter}
        />

        {error ? (
          <ErrorNote zentriert title="Zahlungen konnten nicht geladen werden" message={error} onRetry={paymentsQ.reload} />
        ) : !loading && allRows.length === 0 ? (
          <EmptyState icon="payments" title="Noch keine Zahlungen" hint="Zahlungen legen Sie im jeweiligen Vorgang an." />
        ) : !loading && rows.length === 0 ? (
          // Punkt 115 (Paket 1 „Nie mehr ins Leere“): die Suche bleibt im gewählten Reiter, die
          // Leer-Anzeige nennt aber Treffer in den anderen Reitern (Wechsel BEHÄLT die Suche);
          // Suche aufheben ist ein eigener, sichtbarer Schritt.
          <EmptyState
            icon={search.trim() ? "search_off" : "payments"}
            title={
              statusCounts.all > 0
                ? "Keine Zahlungen in diesem Reiter"
                : search.trim()
                  ? `Keine Treffer für „${search.trim()}“`
                  : "Keine Zahlungen zu diesem Vorgang"
            }
            hint={
              search.trim() && statusCounts.all > 0
                ? `Für „${search.trim()}“ gibt es unter ${STATUS_FILTERS.find((f) => f.key === statusFilter)?.label} nichts.`
                : statusCounts.all > 0
                  ? "Wechsle den Reiter oder zeige alle Zahlungen an."
                  : undefined
            }
            andereTreffer={STATUS_FILTERS.filter((f) => f.key !== "all" && f.key !== statusFilter).map((f) => ({
              label: f.label,
              anzahl: statusCounts[f.key],
              onClick: () => setStatusFilter(f.key),
            }))}
            link={[
              ...(!search.trim() && statusCounts.all > 0
                ? [{ label: "Alle Zahlungen anzeigen", onClick: () => setStatusFilter("all") }]
                : []),
              ...(search.trim() ? [{ label: "In allen Bereichen suchen", onClick: () => oeffneGlobaleSuche(search.trim()) }] : []),
              ...(search.trim() ? [{ label: "Suche aufheben", onClick: () => setSearch("") }] : []),
              ...(vorgangFilter && statusCounts.all === 0 ? [{ label: "Vorgangsfilter aufheben", onClick: vorgangFilterAufheben }] : []),
            ]}
          />
        ) : (
          // Listenstandard (Brief § 2.4): eigener Scrollbereich, damit der Kopf `sticky` stehen
          // bleibt (an der Karte mit `overflow-hidden` scheitert `sticky` still).
          // ponytail: keine Virtualisierung (Plan § Grundbefund); nachrüsten ab ~200 Vorgängen.
          <div className="max-h-[max(20rem,calc(100vh-19rem))] overflow-auto" aria-busy={loading}>
            <div className={narrow ? "" : "min-w-[760px]"}>
              {!narrow && (
              <div role="row" className={`${GRUPPEN_RASTER} sticky top-0 z-[3]`}>
                {GRUPPEN_SPALTEN.map((sp) => (
                  <SortierKopf
                    key={sp.key}
                    als="div"
                    label={sp.label}
                    aktiv={sort.sortKey === sp.key ? sort.dir : null}
                    onClick={() => sort.toggle(sp.key)}
                    rechts={sp.key === "offen"}
                  />
                ))}
              </div>
              )}
              {loading ? (
                <table className="w-full table-fixed border-collapse">
                  <colgroup>
                    <col />
                    <col className="w-[120px]" />
                    <col className="w-[210px]" />
                    <col className="w-[190px]" />
                  </colgroup>
                  <LoadingRows rows={8} spalten={["text", "text", "pill", "text"]} />
                </table>
              ) : (
                <ul>
                  {sichtbareGruppen.map(({ eventId, zeilen, kunde, faelligkeit, offenSumme, bezahltSumme, hatUeberfaellige, statusZeile }) => {
                    const erste = zeilen[0]!;
                    // Befund 12.08.2026 (Antoine, Screenshot storia.schrittmacher.ai/zahlungen): "Event
                    // · Gesamtbetrag" stand doppelt da. `eventLabel` ist serverseitig bereits `occasion
                    // ?? number ?? "Event"`; die Ueberschrift faellt ihrerseits von `eventNumber` auf
                    // `eventLabel` zurueck — die Unterzeile erscheint deshalb nur, wenn sie etwas hinzufuegt.
                    const belegzeile = erste.eventLabel; // Posteingang S2: Anlass vorn, die Nummer steht klein daneben
                    // Antoine, 27.08.2026 (Screenshot storia.schrittmacher.ai/zahlungen): Firmen-/
                    // Personenname gehoert nach vorn, der Platzhalter "Event" (kein Anlass, keine
                    // Vorgangsnummer hinterlegt) klein geschrieben als Nachsatz statt als Ueberschrift —
                    // eine echte Vorgangsnummer/ein echter Anlass bleibt dagegen unveraendert gross.
                    const belegzeileAnzeige = belegzeile === "Event" ? "event" : belegzeile;
                    const deckungshinweis = deckungsSatz(deckungJeVorgang[eventId]);
                    const aufgeklappt = gefiltert || manuellAufgeklappt.has(eventId);
                    const ca = costAcceptanceJeVorgang[eventId];
                    return (
                      <li key={eventId} className="border-t border-[#F6F0E4] first:border-t-0 dark:border-white/[0.06]">
                        {narrow ? (
                          /* Handy-Mockup 26.09.2026: Karte je Auftrag — Kunde und Anlass links, der offene
                             Betrag groß rechts, Status-Pille mit Fälligkeit darunter. Tippen klappt auf. */
                          <button
                            type="button"
                            data-testid={`zahlung-karte-${eventId}`}
                            className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-[#FBF6EC] dark:hover:bg-white/[0.03]"
                            onClick={() => toggleGruppe(eventId)}
                            aria-expanded={aufgeklappt}
                          >
                            <div className="min-w-0 flex-1">
                              <p className="text-[13.5px] font-semibold text-on-background dark:text-inverse-on-surface">{kunde}</p>
                              {/* kein einzelnes „·“, wenn Anlass oder Nummer fehlt */}
                              <p className="text-[11.5px] text-secondary dark:text-secondary-fixed-dim">
                                {[erste.eventLabel !== "Event" ? erste.eventLabel : null, erste.eventNumber !== erste.eventLabel ? erste.eventNumber : null].filter(Boolean).join(" · ")}
                              </p>
                              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                                <StatusPill meta={paymentMeta(statusZeile)} />
                                <span className={`text-[11.5px] tabular-nums ${hatUeberfaellige ? "font-semibold text-[#B23B32] dark:text-[#e39b92]" : "text-secondary dark:text-secondary-fixed-dim"}`}>
                                  {faelligkeit ? `fällig ${formatDate(faelligkeit)}` : statusZeile.paidAt ? `am ${formatDate(statusZeile.paidAt)}` : ""}
                                </span>
                                {ca && <StatusPill meta={CA_STATUS_LABEL[ca.status]} icon="draw" />}
                              </div>
                              {deckungshinweis && (
                                <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]">
                                  <Icon name="info" className="mt-px shrink-0 text-[14px]" />
                                  <span className="min-w-0 break-words">{deckungshinweis}</span>
                                </p>
                              )}
                            </div>
                            <div className="shrink-0 text-right">
                              <p className={`text-[15px] font-bold tabular-nums ${offenSumme > 0 ? "text-on-background dark:text-inverse-on-surface" : "text-secondary dark:text-secondary-fixed-dim"}`}>{money(offenSumme)}</p>
                              <p className="text-[11px] tabular-nums text-secondary dark:text-secondary-fixed-dim">
                                {offenSumme > 0 ? "offen" : bezahltSumme > 0 ? `von ${money(bezahltSumme)}` : ""}
                              </p>
                            </div>
                            <Icon name={aufgeklappt ? "expand_more" : "chevron_right"} className="mt-0.5 shrink-0 text-[18px] text-on-surface-variant/60" />
                          </button>
                        ) : (
                        <button
                          type="button"
                          className={`${GRUPPEN_RASTER} w-full items-start py-3 text-left transition-colors hover:bg-[#FBF6EC] focus-visible:bg-[#FBF6EC] focus-visible:shadow-[inset_3px_0_0_#C2410C] focus-visible:outline-none dark:hover:bg-white/[0.03] dark:focus-visible:bg-white/[0.03]`}
                          onClick={() => toggleGruppe(eventId)}
                          aria-expanded={aufgeklappt}
                        >
                          <div className={`${GRUPPEN_ZELLE} flex items-start gap-2.5`}>
                            <Icon name={aufgeklappt ? "expand_more" : "chevron_right"} className="mt-0.5 shrink-0 text-[18px] text-on-surface-variant/60" />
                            <div className="min-w-0">
                              <span className="flex flex-wrap items-center gap-x-1.5 text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">
                                {kunde}
                                <span className="font-normal text-secondary dark:text-secondary-fixed-dim">
                                  · {belegzeileAnzeige}
                                </span>
                                {erste.eventNumber && (
                                  <span className="text-[11.5px] font-normal text-secondary dark:text-secondary-fixed-dim">{erste.eventNumber}</span>
                                )}
                              </span>
                              {erste.eventLabel !== belegzeile && (
                                <p className="mt-0.5 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{erste.eventLabel}</p>
                              )}
                              {deckungshinweis && (
                                <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-[#F0EAE0] px-2.5 py-1.5 font-body-sm text-[11.5px] text-[#6F675A] dark:bg-white/[0.08]">
                                  <Icon name="info" className="mt-px shrink-0 text-[14px]" />
                                  <span className="min-w-0 break-words">{deckungshinweis}</span>
                                </p>
                              )}
                            </div>
                          </div>
                          <div className={`${GRUPPEN_ZELLE} pt-0.5 text-[13px] tabular-nums`}>
                            {faelligkeit ? (
                              <span className={hatUeberfaellige ? "font-semibold text-[#B23B32] dark:text-[#e39b92]" : "text-on-background dark:text-inverse-on-surface"}>
                                {formatDate(faelligkeit)}
                              </span>
                            ) : (
                              <KeineAngabe />
                            )}
                          </div>
                          <div className={`${GRUPPEN_ZELLE} flex flex-col items-start gap-1`}>
                            <StatusPill meta={paymentMeta(statusZeile)} />
                            {/* Migration 357: permanenter Kostenübernahme-Indikator je Auftrag — nur
                                wenn tatsächlich eine (nicht-Entwurf) Kostenübernahme existiert;
                                "nicht angefordert" bleibt bewusst ausgeblendet statt eines Strichs,
                                damit die Mehrheit der (nicht-B2B-)Aufträge nicht mit Rauschen zugestellt wird. */}
                            {ca && (
                              <span title={ca.signedAt ? `Signiert ${formatDateTime(ca.signedAt)}` : undefined}>
                                <StatusPill meta={CA_STATUS_LABEL[ca.status]} icon="draw" />
                              </span>
                            )}
                          </div>
                          <div className={`${GRUPPEN_ZELLE} text-right`}>
                            {/* Antoine 23.09.2026: nur die Status-Pille sagt den Status — hier steht
                                allein der offene Betrag (früher „Offen · Betrag“ bzw. „Vollständig
                                bezahlt“ als zweite Pille). Überfällig zeigt die rote Fälligkeit. */}
                            <span
                              className={`text-[13px] font-semibold tabular-nums ${offenSumme > 0 ? "text-on-background dark:text-inverse-on-surface" : "text-secondary dark:text-secondary-fixed-dim"}`}
                            >
                              {money(offenSumme)}
                            </span>
                            <p className="mt-1 font-body-sm text-[11px] tabular-nums text-secondary dark:text-secondary-fixed-dim">
                              {zeilen.length} Posten{bezahltSumme > 0 ? ` · ${money(bezahltSumme)} bezahlt` : ""}
                            </p>
                          </div>
                        </button>
                        )}
                        {aufgeklappt && (
                          <ul className="divide-y divide-[#F6F0E4] border-t border-[#F6F0E4] bg-[#FBF8F1]/60 dark:divide-white/[0.05] dark:border-white/[0.06] dark:bg-white/[0.02]">
                            {zeilen.map((p) => (
                              <li
                                key={p.id}
                                tabIndex={0}
                                className="flex cursor-pointer flex-wrap items-center justify-between gap-3 py-3 pl-[46px] pr-[18px] max-sm:pl-4 transition-colors hover:bg-[#FBF6EC] focus-visible:bg-[#FBF6EC] focus-visible:shadow-[inset_3px_0_0_#C2410C] focus-visible:outline-none dark:hover:bg-white/[0.03] dark:focus-visible:bg-white/[0.03]"
                                onClick={() => setSelected(p)}
                                onKeyDown={(e) => {
                                  // Brief § 2.3: Enter/Leertaste öffnet den Posten wie ein Klick.
                                  if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                                    e.preventDefault();
                                    setSelected(p);
                                  }
                                }}
                              >
                                <div className="min-w-0">
                                  <p className="font-body-sm text-body-sm font-medium text-on-background dark:text-inverse-on-surface">
                                    {PAYMENT_TYPE_LABEL[p.paymentType] ?? p.paymentType}
                                  </p>
                                  <p className="mt-0.5 font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">
                                    {p.paidAt ? formatDate(p.paidAt) : p.dueDate ? `fällig ${formatDate(p.dueDate)}` : "–"}
                                    {p.stripePaymentIntentId ? " · Stripe · Karte" : " · Überweisung"}
                                  </p>
                                </div>
                                <div className="flex shrink-0 items-center gap-3">
                                  <span className="font-body-sm text-body-sm font-semibold tabular-nums text-on-background dark:text-inverse-on-surface">{money(p.amountCents)}</span>
                                  <StatusPill meta={paymentMeta(p)} />
                                </div>
                              </li>
                            ))}
                            {/* Direkte Sprungziele bleiben erreichbar, ohne den Drawer erst zu oeffnen. */}
                            <li className="flex items-center gap-4 py-2.5 pl-[46px] pr-[18px] font-body-sm text-[11.5px] max-sm:pl-4">
                              <Link to={mitHier(vorgangPfad(eventId), "Zahlungen")} className="rounded-md text-primary hover:underline">
                                Zum Auftrag
                              </Link>
                              <Link to={mitHier(`/kunden/${erste.customerId}`, "Zahlungen")} className="rounded-md text-primary hover:underline">
                                Zum Kunden
                              </Link>
                            </li>
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        )}
        {!error && (
          <ListFooter
            anzahl={rows.length}
            gesamt={allRows.length}
            einheit={allRows.length === 1 ? "Zahlung" : "Zahlungen"}
            zusatz={
              <>
                {gruppen.length.toLocaleString("de-DE")} {gruppen.length === 1 ? "Auftrag" : "Aufträge"}
                {openSum > 0 && (
                  <>
                    {" · "}Offen gesamt: <strong className="font-semibold text-[#262119] dark:text-inverse-on-surface">{money(openSum)}</strong>
                    {overdueSum > 0 && <span className="text-error"> · davon überfällig {money(overdueSum)}</span>}
                  </>
                )}
              </>
            }
            sortiertNach={sort.spalte?.label ?? null}
            richtung={sort.dir}
            onReset={sort.reset}
            ohneSortierHinweis={narrow}
          />
        )}
      </Card>

      {/* Detail-Drawer (Quick-Win 2026-07): Kopf, Stripe-Referenzen, Kostenübernahme-/Rechnung-Link,
          Timeline, Event-Link. Rein lesend — GET /api/payments/:id/detail berechnet/verändert keine
          Beträge, liefert nur bereits gespeicherte Werte. Gleiches Drawer-Muster wie /anfragen. */}
      <Drawer
        open={selected != null}
        onClose={() => setSelected(null)}
        title={selected ? `${PAYMENT_TYPE_LABEL[selected.paymentType] ?? selected.paymentType} · ${selected.eventLabel}` : "Zahlung"}
        subtitle={selected ? (selected.eventNumber ?? undefined) : undefined}
        headerAccessory={selected ? <StatusPill meta={paymentMeta(selected)} /> : undefined}
        footer={
          selected && (
            <div className="flex w-full items-center gap-2.5">
              {/* Bisheriger Beleg-Deep-Link bleibt als Sekundär-Aktion erhalten (Klick auf die
                  Tabellenzeile selbst navigiert nicht mehr direkt, s.o.). */}
              <ButtonRouterLink to={mitHier(vorgangPfad(selected.eventId), "Zahlungen")} variant="secondary" className="flex-1 justify-center py-2.5">
                <Icon name="open_in_new" className="text-[18px]" /> Zum Vorgang
              </ButtonRouterLink>
              {detail && detail.event && detail.event.currentOfferVersion > 0 && (
                <ButtonRouterLink to={angebotPfad(selected.eventId)} variant="primary" className="flex-1 justify-center py-2.5">
                  <Icon name="request_quote" className="text-[18px]" /> Zum Angebot
                </ButtonRouterLink>
              )}
            </div>
          )
        }
      >
        {selected && (
          <div className="space-y-[18px]">
            {detailQ.error && <ErrorNote message={detailQ.error} />}

            {/* Kopf-Eckdaten — sofort aus der Listenzeile verfügbar, kein Warten auf den Detail-Request. */}
            <div className="grid grid-cols-2 gap-x-4 gap-y-3.5 px-0.5 sm:grid-cols-3">
              <Fact label="Betrag" value={money(selected.amountCents)} />
              <Fact label="Fällig" value={selected.dueDate ? formatDate(selected.dueDate) : "–"} />
              <Fact label="Bezahlt am" value={selected.paidAt ? formatDateTime(selected.paidAt) : "–"} />
              <Fact label="Anlass" value={(detail && anlassAnzeige(detail.event?.occasion)) ?? selected.eventLabel} />
              <Fact
                label="Angebotsversion"
                value={detail && detail.event ? (detail.event.currentOfferVersion > 0 ? `v${detail.event.currentOfferVersion}` : "kein Angebot") : "–"}
              />
            </div>

            {!detail && detailQ.loading ? (
              <LoadingRows rows={3} />
            ) : (
              <>
                {/* Stripe-Referenzen */}
                <div className="rounded-xl border border-surface-variant bg-surface-container-low p-3.5 dark:border-white/10 dark:bg-surface-variant/10">
                  <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">Stripe-Referenzen</p>
                  {detail &&
                  (detail.payment.stripePaymentIntentId ||
                    detail.payment.stripePaymentLinkId ||
                    detail.payment.stripeCheckoutSessionId ||
                    detail.payment.checkoutUrl) ? (
                    <div className="space-y-1.5">
                      {detail.payment.stripePaymentIntentId && (
                        <p className="font-mono text-[11.5px] text-on-surface dark:text-inverse-on-surface">
                          PaymentIntent · {shortRef(detail.payment.stripePaymentIntentId)}
                        </p>
                      )}
                      {detail.payment.stripeCheckoutSessionId && (
                        <p className="font-mono text-[11.5px] text-on-surface dark:text-inverse-on-surface">
                          Checkout-Session · {shortRef(detail.payment.stripeCheckoutSessionId)}
                        </p>
                      )}
                      {detail.payment.stripePaymentLinkId && (
                        <p className="font-mono text-[11.5px] text-on-surface dark:text-inverse-on-surface">
                          Zahlungslink · {shortRef(detail.payment.stripePaymentLinkId)}
                        </p>
                      )}
                      {detail.payment.checkoutUrl && (
                        <a
                          href={detail.payment.checkoutUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1 flex items-center gap-1 font-body-sm text-[11.5px] text-primary hover:underline"
                        >
                          <Icon name="open_in_new" className="text-[14px]" /> Zahlungslink öffnen
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="font-body-sm text-[12px] text-on-surface-variant">
                      Kein Stripe-Zahlungsweg hinterlegt (Überweisung oder noch kein Zahlungslink erstellt).
                    </p>
                  )}
                </div>

                {/* Kostenübernahme */}
                <div className="rounded-xl border border-surface-variant bg-surface-container-low p-3.5 dark:border-white/10 dark:bg-surface-variant/10">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">Kostenübernahme</p>
                    {detail && detail.costAcceptance && <StatusPill meta={CA_STATUS_LABEL[detail.costAcceptance.status]} />}
                  </div>
                  {detail && detail.costAcceptance ? (
                    <div className="font-body-sm text-[12.5px] text-on-surface dark:text-inverse-on-surface">
                      <p>
                        {detail.costAcceptance.signerName ?? "—"}
                        {detail.costAcceptance.amountGrossCents != null
                          ? ` · ${money(detail.costAcceptance.amountGrossCents)}`
                          : " · A-la-carte, kein fester Endbetrag"}
                      </p>
                      {detail.costAcceptance.signedAt && (
                        <p className="mt-0.5 text-[11px] text-on-surface-variant/70">Signiert {formatDateTime(detail.costAcceptance.signedAt)}</p>
                      )}
                      {detail.costAcceptance.signerEmail && <p className="mt-0.5 text-[11px] text-on-surface-variant/70">{detail.costAcceptance.signerEmail}</p>}
                      {detail.costAcceptance.signerMobile && <p className="mt-0.5 text-[11px] text-on-surface-variant/70">{detail.costAcceptance.signerMobile}</p>}
                      {costAcceptanceMfaHinweis(detail.costAcceptance.mfaMethods) && (
                        <p className="mt-0.5 text-[11px] text-on-surface-variant/70">{costAcceptanceMfaHinweis(detail.costAcceptance.mfaMethods)}</p>
                      )}
                      {detail.costAcceptance.signedPdfSha256 ? (
                        // M-24 Nachtrag: eigenes Archiv statt der (ablaufenden) Provider-URL -
                        // owner/admin-geschuetzter Download ueber Blob-URL (lib/files.ts).
                        <button
                          type="button"
                          onClick={() => void openAuthenticatedFile(`/api/events/${selected!.eventId}/cost-acceptance/pdf`)}
                          className="mt-1.5 inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Icon name="picture_as_pdf" className="text-[14px]" /> Unterschriebenes PDF
                        </button>
                      ) : (
                        detail.costAcceptance.signedPdfUrl && (
                          <a
                            href={detail.costAcceptance.signedPdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-1.5 inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <Icon name="picture_as_pdf" className="text-[14px]" /> Unterschriebenes PDF
                          </a>
                        )
                      )}
                    </div>
                  ) : (
                    <p className="font-body-sm text-[12px] text-on-surface-variant">Nicht angefordert.</p>
                  )}
                </div>

                {/* Rechnung (nur die Auto-Rechnung DIESER Zahlung, siehe invoices.payment_id) */}
                <div className="rounded-xl border border-surface-variant bg-surface-container-low p-3.5 dark:border-white/10 dark:bg-surface-variant/10">
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">Rechnung</p>
                    {detail && detail.invoice && <StatusPill meta={INV_STATUS_LABEL[detail.invoice.status]} />}
                  </div>
                  {detail && detail.invoice ? (
                    <div className="font-body-sm text-[12.5px] text-on-surface dark:text-inverse-on-surface">
                      <p>
                        {INV_TYPE_LABEL[detail.invoice.invoiceType]}
                        {detail.invoice.invoiceNumber ? ` · ${detail.invoice.invoiceNumber}` : ""}
                        {detail.invoice.amountGrossCents != null ? ` · ${money(detail.invoice.amountGrossCents)}` : ""}
                      </p>
                      {detail.invoice.pdfUrl && (
                        <a
                          href={detail.invoice.pdfUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-1.5 inline-flex items-center gap-1 text-primary hover:underline"
                        >
                          <Icon name="picture_as_pdf" className="text-[14px]" /> Rechnungs-PDF
                        </a>
                      )}
                    </div>
                  ) : (
                    <p className="font-body-sm text-[12px] text-on-surface-variant">Keine automatisch erzeugte Rechnung verknüpft.</p>
                  )}
                </div>

                {/* Timeline (activity_log, chronologisch) */}
                {detail && detail.timeline.length > 0 && (
                  <div>
                    <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-on-surface-variant/70">Timeline</p>
                    <div className="flex flex-col">
                      {detail.timeline.map((ev, i) => {
                        const last = i === detail.timeline.length - 1;
                        return (
                          <div key={ev.id} className="flex gap-3">
                            <div className="flex shrink-0 flex-col items-center">
                              <span className={`mt-1 h-[9px] w-[9px] rounded-full ${last ? "bg-primary" : "bg-outline-variant"}`} />
                              {!last && <span className="w-px flex-1 bg-outline-variant/60" />}
                            </div>
                            <div className={last ? "" : "pb-3.5"}>
                              <p className="font-body-sm text-[12.5px] font-medium text-on-surface dark:text-inverse-on-surface">
                                {zahlungsEingangText(ev)}
                              </p>
                              <p className="mt-0.5 font-body-sm text-[11px] text-on-surface-variant/70">
                                {formatDateTime(ev.createdAt)} · {ev.actorLabel ?? ACTOR_LABEL[ev.actorType]}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}
